(() => {
  'use strict';
  if (customElements.get('bina-app-prompt')) return;
  const KEY = 'bina:android-app:dismissed-until:v1';
  let memoryUntil = 0;
  const readUntil = () => {
    let until = memoryUntil;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { until = Math.max(until, Number(window[name].getItem(KEY)) || 0); } catch (_) { /* Storage may be blocked. */ }
    }
    return until;
  };
  const remember = days => {
    memoryUntil = Date.now() + days * 86400000;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem(KEY, String(memoryUntil)); } catch (_) { /* Memory fallback still prevents repeat prompts on this page. */ }
    }
  };
  customElements.define('bina-app-prompt', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      this.media = window.matchMedia('(max-width: 749px)');
      this.days = Math.min(30, Math.max(1, Number(this.dataset.days) || 7));
      this.preview = Boolean(window.Shopify && Shopify.designMode && this.dataset.editorPreview === 'true');
      const options = { signal: this.controller.signal };
      this.querySelectorAll('[data-app-dismiss]').forEach(button => button.addEventListener('click', () => this.dismiss(), options));
      this.querySelector('[data-app-download]').addEventListener('click', () => this.dismiss(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); }, options);
      this.dialog.addEventListener('close', () => this.restoreFocus(), options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.media.addEventListener('change', () => {
        if (!this.media.matches) { this.pending = false; this.stopWatching(); if (this.dialog.open) this.dialog.close(); }
      }, options);
      window.addEventListener('storage', () => {
        if (!this.preview && readUntil() > Date.now()) { this.pending = false; this.stopWatching(); if (this.dialog.open) this.dialog.close(); }
      }, options);
      if (!this.eligible()) return;
      const delay = Math.min(10, Math.max(0, Number(this.dataset.delay) || 0)) * 1000;
      const start = () => { this.timer = window.setTimeout(() => this.startWatching(), delay); };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { ...options, once: true });
    }
    eligible() {
      const ua = navigator.userAgent || '';
      const androidPhone = /Android/i.test(ua) && /Mobile/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua);
      const embedded = /; wv\)|\bBinasArtApp\b|\bBinasArt\/|Shopify Mobile/i.test(ua);
      const sensitiveRoute = /\/(?:checkouts?|cart|account|challenge)(?:\/|$)/i.test(location.pathname);
      return this.media.matches && !sensitiveRoute && (this.preview || (androidPhone && !embedded && readUntil() <= Date.now()));
    }
    visible(element) {
      if (!element || element === this || this.contains(element) || !element.getClientRects().length) return false;
      const style = getComputedStyle(element);
      return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) !== 0;
    }
    blocked() {
      if (document.visibilityState !== 'visible') return true;
      const active = document.activeElement;
      if (active && active.matches('input,textarea,select,[contenteditable="true"]')) return true;
      const selectors = 'dialog[open],[role="dialog"][aria-modal="true"],cart-drawer.active,cart-notification.active,header details[open],menu-drawer details[open],details-modal details[open],.shopify-pc__banner,#shopify-pc__banner,.shopify-pc__prefs,#shopify-pc__prefs,.modal--is-active,.popup--active,[data-popup-open="true"],.needsclick[role="dialog"]';
      if ([...document.querySelectorAll(selectors)].some(element => {
        if (!this.visible(element)) return false;
        if (element.matches('details') && element.closest('.bina-footer-menu,bina-footer-menu')) return false;
        return true;
      })) return true;
      // Also defer to visible third-party fixed popups, including cross-origin iframe overlays.
      return [...document.querySelectorAll('iframe,[class*="popup"],[class*="modal"],[id*="cookie"],[class*="cookie"]')].some(element => {
        if (!this.visible(element)) return false;
        const style = getComputedStyle(element), rect = element.getBoundingClientRect();
        return style.position === 'fixed' && rect.width > 200 && rect.height > 100 && rect.bottom > 0 && rect.top < window.innerHeight;
      });
    }
    startWatching() {
      if (!this.eligible()) return;
      this.pending = true;
      const queue = () => {
        if (this.checkTimer || !this.pending) return;
        this.checkTimer = window.setTimeout(() => { this.checkTimer = 0; this.tryOpen(); }, 150);
      };
      this.observer = new MutationObserver(queue);
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'hidden', 'aria-hidden', 'aria-modal'] });
      document.addEventListener('focusout', queue, { signal: this.controller.signal });
      document.addEventListener('visibilitychange', queue, { signal: this.controller.signal });
      this.expiryTimer = window.setTimeout(() => { this.pending = false; this.stopWatching(); }, 120000);
      this.tryOpen();
    }
    tryOpen() {
      if (!this.pending || !this.eligible() || this.blocked()) return;
      this.pending = false;
      this.stopWatching();
      this.previousFocus = document.activeElement;
      // Native dialog's top layer and backdrop isolate floating app buttons without altering them.
      try {
        this.dialog.showModal();
        this.querySelector('.bina-app-dialog__continue').focus({ preventScroll: true });
      } catch (_) { /* Unsupported/removed dialog: fail closed without affecting shopping. */ }
    }
    dismiss() {
      if (!this.preview) remember(this.days);
      this.pending = false;
      this.stopWatching();
      if (this.dialog.open) this.dialog.close();
    }
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll('a[href],button:not([disabled])')];
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    restoreFocus() {
      if (this.previousFocus && this.previousFocus.isConnected && this.previousFocus !== document.body) this.previousFocus.focus({ preventScroll: true });
    }
    stopWatching() {
      if (this.observer) this.observer.disconnect();
      window.clearTimeout(this.checkTimer);
      window.clearTimeout(this.expiryTimer);
      this.checkTimer = 0;
    }
    disconnectedCallback() {
      this.pending = false;
      this.stopWatching();
      window.clearTimeout(this.timer);
      if (this.dialog && this.dialog.open) this.dialog.close();
      if (this.controller) this.controller.abort();
      this.controller = null;
    }
  });
})();
